import { reaction } from 'mobx';

import { tableStore } from '../../../stores/table-store';

/**
 * Enforce "one sidebar panel at a time" from the STORE side.
 *
 * ## Why this exists
 *
 * The rule (behavior contract §1) is enforced in TWO places, and missing
 * either one is invisible until you see two sidebars at once:
 *
 * 1. `setExpandedCellViewOpen` closes `keyboardShortcutsOpen` — both are store
 *    flags, so the setter can do it.
 * 2. Supervisor and Resource Library are REACT STATE in `table.tsx`, rendered
 *    as sibling fixed-500px Panels. The store cannot see or close them, so
 *    every opener has had to call `closeOverlaySidePanels()` itself.
 *
 * Bobcat's click-to-open reaches the store directly from `cell.tsx` and so had
 * no way to satisfy (2): with the flag on, opening Supervisor and then clicking
 * a cell mounted a second 500px sidebar and squeezed the table. Reacting to the
 * flag covers that path and any future one, instead of relying on each new
 * opener remembering.
 *
 * ## Why it is a standalone function
 *
 * It was written inline in `table.tsx`, where testing it means rendering a
 * 3,169-line component. As a plain function taking the closer and returning
 * mobx's disposer, it is testable with no React at all — the same reason
 * `should-toggle.ts` and `should-open-on-click.ts` sit beside it.
 *
 * @param closeOverlaySidePanels closes the React-state panels (Supervisor /
 *   Resource Library).
 * @returns the mobx disposer — hand it straight back from a `useEffect`.
 */
export const wireOneSidebarPanelAtATime = (
  closeOverlaySidePanels: () => void
): (() => void) =>
  reaction(
    () => tableStore.expandedCellViewOpen,
    (open) => {
      // Only on OPEN. Closing the panel must not touch the others, or
      // dismissing this one would clear a panel the user opened deliberately.
      if (open) closeOverlaySidePanels();
    }
  );
